import React, { useState, useEffect } from 'react';

const ipc = window.electronAPI;

/**
 * DatabaseModeToggle
 *
 * A pill-style toggle that lets users switch between the original Ecoinvent
 * TRACI lookup table and an error-induced "noisy" variant (±10% per score).
 *
 * Props:
 *   onModeChange(mode: 'original' | 'noisy') — called whenever the mode changes
 */
const DatabaseModeToggle = ({ onModeChange }) => {
  const [mode, setMode] = useState('original');
  const [loading, setLoading] = useState(true);

  // Sync with backend on mount
  useEffect(() => {
    ipc.invoke('get-traci-db-mode')
      .then(res => {
        const m = res?.mode || 'original';
        setMode(m);
        onModeChange?.(m);
      })
      .catch(() => {/* default to original */})
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    onModeChange?.(newMode);
    try {
      await ipc.invoke('set-traci-db-mode', newMode);
    } catch (err) {
      console.error('[DatabaseModeToggle] Failed to set mode:', err);
    }
  };

  const isNoisy = mode === 'noisy';

  const containerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px',
    background: isNoisy ? '#fff8e1' : '#f8f9fa',
    border: isNoisy ? '1px solid #ffa000' : '1px solid #dee2e6',
    borderRadius: '20px',
    transition: 'all 0.2s ease',
    userSelect: 'none',
  };

  const pillStyle = {
    display: 'inline-flex',
    background: '#e9ecef',
    borderRadius: '12px',
    padding: '2px',
    gap: '2px',
  };

  const btnBase = {
    padding: '3px 10px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 500,
    cursor: loading ? 'default' : 'pointer',
    transition: 'all 0.15s ease',
  };

  const btnOriginal = {
    ...btnBase,
    background: !isNoisy ? '#fff' : 'transparent',
    color: !isNoisy ? '#2e7d32' : '#888',
    boxShadow: !isNoisy ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
  };

  const btnNoisy = {
    ...btnBase,
    background: isNoisy ? '#fff' : 'transparent',
    color: isNoisy ? '#e65100' : '#888',
    boxShadow: isNoisy ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
  };

  const labelStyle = {
    fontSize: '11px',
    color: isNoisy ? '#e65100' : '#666',
    fontWeight: isNoisy ? 600 : 400,
    whiteSpace: 'nowrap',
  };

  return (
    <div style={containerStyle} title="Switch between the original and error-induced (±10%) TRACI lookup table">
      <span style={labelStyle}>
        {isNoisy ? '⚠ Noisy DB active' : 'TRACI DB:'}
      </span>
      <div style={pillStyle}>
        <button
          style={btnOriginal}
          onClick={() => handleToggle('original')}
          disabled={loading}
          id="traci-db-toggle-original"
          title="Use verified Ecoinvent TRACI scores"
        >
          Original
        </button>
        <button
          style={btnNoisy}
          onClick={() => handleToggle('noisy')}
          disabled={loading}
          id="traci-db-toggle-noisy"
          title="Use error-induced scores (±10% uniform noise, seed=42)"
        >
          Noisy ±10%
        </button>
      </div>
    </div>
  );
};

export default DatabaseModeToggle;
